import React, { useEffect } from "react";
import { useState, useRef } from "react";
import { Helmet } from 'react-helmet';
import { useNavigate } from "react-router-dom";
import { baseUrl as apiBaseUrl } from "../api/axiosClient";

const googlePlayUrl =
  "https://play.google.com/store/apps/details?id=com.caloriespal.app";

const Home = (props) => {
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  let servicesBlock = useRef();
  let howitworksBlock = useRef();
  let contactusBlock = useRef();
  const [status, setStatus] = useState("Submit");
  const [foodSearch, setFoodSearch] = useState("");
  const [iosComingSoonOpen, setIosComingSoonOpen] = useState(false);
  const siteBaseUrl = "https://www.caloriespal.com";
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CaloriesPal",
    url: siteBaseUrl,
    logo: `${siteBaseUrl}/assets/images/Calories (1).png`,
    sameAs: [
      "https://www.facebook.com/caloriespal",
      "https://www.instagram.com/caloriespal",
      "https://twitter.com/CaloriesPal",
      "https://www.linkedin.com/company/caloriespal",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "contact@caloriespal.com",
      areaServed: "Worldwide",
      availableLanguage: ["English", "Arabic"],
    },
  };
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "CaloriesPal",
    url: siteBaseUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteBaseUrl}/foods?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteBaseUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Food Explorer",
        item: `${siteBaseUrl}/foods`,
      },
    ],
  };
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const handleSignin = () => {
    if (isAuthenticated) {
      navigate("/app/dashboard");
    } else {
      navigate("/login");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(Boolean(token));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("Sending...");

    const { name, email, message, company, country } = e.target.elements;
    let details = {
      name: name.value,
      email: email.value,
      message: message.value,
      company: company.value,
      country: country.value,
    };

    let response = await fetch(apiBaseUrl + "contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
      },
      body: JSON.stringify(details),
    });
    setStatus("Submit");
    let result = await response.json();
  };

  const handleFoodSearch = (e) => {
    e.preventDefault();
    const trimmed = foodSearch.trim();
    if (trimmed) {
      navigate(`/foods?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate("/foods");
    }
  };

  const trackAppDownloadClick = async (platform) => {
    try {
      await fetch(`${apiBaseUrl}public/app-download-clicks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=utf-8",
        },
        body: JSON.stringify({ platform }),
      });
    } catch (error) {
      console.error("Unable to track app download click", error);
    }
  };

  const handleGooglePlayClick = () => {
    trackAppDownloadClick("google_play");
  };

  const handleIosClick = async () => {
    await trackAppDownloadClick("ios");
    setIosComingSoonOpen(true);
  };

  return (
    <>
      <Helmet>
        {/* Standard meta tags */}
        <title>CaloriesPal | Calorie Tracker with Clarity in Every Bite</title>
        <meta name="description" content="Introducing a comprehensive, innovative, and user-friendly solution for all your food labeling needs. Our reliable and customizable service makes it easy to comply with regulations and provide accurate and transparent information to consumers." />
        <meta name="keywords" content="american nutrition labels, USDA food explorer, FDA compliant recipes, barbecue sauce nutrition, peanut butter calories, burger macros, tex mex meal planning, cold brew stats, kombucha tracker, CaloriesPal" />

        <meta name="author" content="CaloriesPal Inc." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="robots" content="index, follow" />
        <meta name="theme-color" content="#84cc16" />
        <meta name="application-name" content="CaloriesPal" />
        <meta name="apple-mobile-web-app-title" content="CaloriesPal" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content="CaloriesPal | Calorie Tracker with Clarity in Every Bite" />
        <meta property="og:description" content="Introducing a comprehensive, innovative, and user-friendly solution for all your food labeling needs. Our reliable and customizable service makes it easy to comply with regulations and provide accurate and transparent information to consumers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteBaseUrl} />
        <meta property="og:image" content={process.env.PUBLIC_URL + "/assets/images/Calories (1).png"} />
        <meta property="og:image:alt" content="CaloriesPal dashboard and nutrition label preview" />
        <meta property="og:site_name" content="CaloriesPal" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CaloriesPal | Calorie Tracker with Clarity in Every Bite" />
        <meta name="twitter:description" content="Introducing a comprehensive, innovative, and user-friendly solution for all your food labeling needs. Our reliable and customizable service makes it easy to comply with regulations and provide accurate and transparent information to consumers." />
        <meta name="twitter:image" content={process.env.PUBLIC_URL + "/assets/images/Calories (1).png"} />
        <meta name="twitter:site" content="@CaloriesPal" />
        <meta name="twitter:creator" content="@CaloriesPal" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={siteBaseUrl} />
        <link rel="alternate" hrefLang="en" href={siteBaseUrl} />
        <script type="application/ld+json">
          {JSON.stringify([organizationSchema, websiteSchema, breadcrumbSchema])}
        </script>
      </Helmet>

      <div className="relative w-full h-full pb-10">
        <div className="hidden md:block">
          <img
            className="absolute bg-cover bg-center w-full h-full inset-0"
            src={process.env.PUBLIC_URL + "/assets/images/hero-banner.jpg"}
          />
        </div>
        <nav className="lg:hidden relative z-50">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setMenu(true)}
              className="p-2 rounded-xl border border-white/30 bg-black/40 text-white focus:outline-none focus:ring-2 focus:ring-lime-400"
              aria-label="Open navigation"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="15" y2="18" />
              </svg>
            </button>
            <img
              src={process.env.PUBLIC_URL + "/assets/images/Calories (1).png"}
              alt="CaloriesPal"
              className="h-8"
            />
            <button
              onClick={handleSignin}
              className="px-4 py-2 rounded-full bg-lime-500 text-slate-900 text-sm font-semibold shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lime-300"
            >
              {isAuthenticated ? "Dashboard" : "Sign In"}
            </button>
          </div>

          <div
            className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition ${
              menu ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
            onClick={() => setMenu(false)}
          />
          <div
            className={`fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 text-white transform transition-transform ${
              menu ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <img
                  src={process.env.PUBLIC_URL + "/assets/images/Calories (1).png"}
                  alt="CaloriesPal"
                  className="h-10 w-10 rounded-2xl bg-white/10 p-1"
                />
                <div>
                  <p className="text-xs text-slate-400">CaloriesPal</p>
                  <p className="text-lg font-semibold">Creator Hub</p>
                </div>
              </div>
              <button
                onClick={() => setMenu(false)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 focus:outline-none text-lg"
                aria-label="Close navigation"
              >
                X
              </button>
            </div>
            <div className="flex flex-col divide-y divide-white/10">
              {[
                {
                  label: "Services",
                  action: () =>
                    window.scrollTo({ top: servicesBlock.current.offsetTop, behavior: "smooth" }),
                },
                {
                  label: "How it Works",
                  action: () =>
                    window.scrollTo({ top: howitworksBlock.current.offsetTop, behavior: "smooth" }),
                },
                { label: "Foods", action: () => navigate("/foods") },
                {
                  label: "Contact",
                  action: () =>
                    window.scrollTo({ top: contactusBlock.current.offsetTop, behavior: "smooth" }),
                },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setMenu(false);
                    item.action();
                  }}
                  className="flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 focus:outline-none"
                >
                  <span className="font-semibold">{item.label}</span>
                  <span className="text-slate-500">{">"}</span>
                </button>
              ))}
              {!isAuthenticated && (
                <button
                  onClick={() => {
                    setMenu(false);
                    navigate("/signup");
                  }}
                  className="px-5 py-4 text-left font-semibold hover:bg-white/5 focus:outline-none cta-heartbeat"
                >
                  Sign up for free
                </button>
              )}
              <button
                onClick={() => {
                  setMenu(false);
                  handleSignin();
                }}
                className="px-5 py-4 text-left font-semibold hover:bg-white/5 focus:outline-none"
              >
                {isAuthenticated ? "Dashboard" : "Sign In"}
              </button>
            </div>
          </div>
        </nav>
        {/* Desktop Version */}
        <nav className="f-f-l relative z-10">
          <div className="relative z-10 mx-auto container hidden w-full px-4 xl:px-0 lg:flex justify-between items-center py-11">
            <div>
              <img
                src={process.env.PUBLIC_URL + "/assets/images/Calories (1).png"}
                alt="logo"
              />
            </div>
            <div className="flex items-center text-white text-base font-medium">
              <ul className="flex items-center pr-3 xl:pr-12">
                <li className="pl-3 lg:pl-5 xl:pl-8 cursor-pointer hover:text-gray-300 ease-in">
                  <a
                    className="ml-2 focus:outline-none focus:ring-2 focus:ring-black"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        behavior: "smooth",
                        top: servicesBlock.current.offsetTop,
                      });
                    }}
                  >
                    <span className="font-bold">Services</span>
                  </a>
                </li>
                <li className="pl-3 lg:pl-5 xl:pl-8 cursor-pointer hover:text-gray-300 ease-in">
                  <a
                    href="/foods"
                    className="focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    Foods
                  </a>
                </li>
                <li className="pl-3 lg:pl-5 xl:pl-8 cursor-pointer hover:text-gray-300 ease-in">
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        behavior: "smooth",
                        top: howitworksBlock.current.offsetTop,
                      });
                    }}
                    className="focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    How it Works
                  </a>
                </li>
                <li className="pl-3 lg:pl-5 xl:pl-8 cursor-pointer hover:text-gray-300 ease-in">
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        behavior: "smooth",
                        top: contactusBlock.current.offsetTop,
                      });
                    }}
                    className="focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    Contact us
                  </a>
                </li>
                {!isAuthenticated && (
                  <li className="pl-3 lg:pl-5 xl:pl-8 cursor-pointer hover:text-gray-300 ease-in">
                    <button
                      onClick={() => navigate("/signup")}
                      className="px-6 py-3 bg-rose-500 hover:bg-rose-600 text-white text-base font-medium rounded-lg flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-300 cta-heartbeat"
                    >
                      Sign up for free
                    </button>
                  </li>
                )}
              </ul>
              
              <button
                onClick={handleSignin}
                className="px-6 py-3 bg-lime-700 hover:bg-lime-900 text-white text-base font-medium rounded-lg flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700"
              >
                <span>{isAuthenticated ? "Dashboard" : "Sign In"}</span>
                <svg
                  className="ml-3 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </nav>
        <div className="relative px-4 xl:px-0 container mx-auto md:flex items-center gap-8">
          <div className="text-color w-full md:w-1/3 pt-16 lg:pt-32 xl:pt-12">
            <h1 className="text-4xl md:text-4xl lg:text-6xl w-11/12 lg:w-11/12 xl:w-full xl:text-6xl text-gray-900 font-extrabold f-f-l">
              Clarity in Every bite
            </h1>
            <div className="f-f-r text-base lg:text-base pb-20 sm:pb-0 pt-10 xl:pt-6">
              <h2>
                Introducing a comprehensive, innovative, and user-friendly
                solution for all your food labeling needs. Our reliable and
                customizable service makes it easy to comply with regulations
                and provide accurate and transparent information to consumers.
              </h2>
            </div>
            <div className="mt-6 w-full rounded-3xl border border-lime-100/60 bg-white/85 backdrop-blur-sm shadow-lg shadow-lime-200/30">
              <div className="rounded-[26px] p-5 sm:p-6 space-y-2">
                <p className="text-xs uppercase tracking-[0.35em] text-lime-600">
                  Food Explorer
                </p>
                <h3 className="text-xl font-semibold text-gray-900">
                  Search the nutrient database instantly
                </h3>
                <p className="text-sm text-gray-600">
                  Jump straight to the public food page with detailed nutrition stats.
                </p>
                <form className="space-y-3 pt-1" onSubmit={handleFoodSearch}>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                      🔍
                    </span>
                    <input
                      type="text"
                      value={foodSearch}
                      onChange={(e) => setFoodSearch(e.target.value)}
                      className="w-full rounded-full border border-gray-200 bg-white px-12 pr-32 py-4 text-gray-900 shadow-md focus:outline-none focus:ring-2 focus:ring-lime-300"
                      placeholder="Try avocado toast, BBQ sauce, kombucha..."
                    />
                    <div className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 uppercase tracking-[0.35em]">
                      Instant
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      <span className="px-3 py-1 rounded-full bg-gray-100">Trending: Peanut Butter</span>
                      <span className="px-3 py-1 rounded-full bg-gray-100">Cold Brew</span>
                      <span className="px-3 py-1 rounded-full bg-gray-100">Tex-Mex Salsa</span>
                    </div>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center rounded-full border border-lime-400 text-lime-700 font-semibold px-6 py-3 bg-white hover:bg-lime-50 transition"
                    >
                      Launch Food Explorer
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="mt-5 rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-lg shadow-slate-200/40">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-3">
                Mobile app
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleGooglePlayClick}
                  className="inline-flex flex-1 items-center justify-center gap-3 rounded-2xl bg-slate-950 px-5 py-3 text-left text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-lime-300"
                >
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M4.5 3.5L14.4 12L4.5 20.5V3.5Z" fill="#34A853" />
                    <path d="M14.4 12L17.1 9.7L20.3 11.5C20.7 11.7 20.7 12.3 20.3 12.5L17.1 14.3L14.4 12Z" fill="#FBBC04" />
                    <path d="M4.5 3.5L17.1 9.7L14.4 12L4.5 3.5Z" fill="#4285F4" />
                    <path d="M4.5 20.5L14.4 12L17.1 14.3L4.5 20.5Z" fill="#EA4335" />
                  </svg>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.24em] text-slate-300">
                      Download from
                    </span>
                    <span className="block text-base font-semibold leading-tight">
                      Google Play
                    </span>
                  </span>
                </a>
                <button
                  type="button"
                  onClick={handleIosClick}
                  className="inline-flex flex-1 items-center justify-center gap-3 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-left text-slate-950 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-lime-300"
                >
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M16.6 12.4c0-2.1 1.7-3.1 1.8-3.2-1-1.5-2.5-1.7-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.6-.8-2.7-.8-1.4 0-2.7.8-3.5 2.1-1.5 2.7-.4 6.6 1.1 8.8.7 1.1 1.6 2.3 2.7 2.2 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1.1 2.7-2.2.8-1.2 1.1-2.3 1.1-2.4-.1 0-2.7-1-2.7-3.6Z" />
                    <path d="M14.6 6.1c.6-.8 1.1-1.8.9-2.8-.9 0-2 .6-2.6 1.3-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.6-1.2Z" />
                  </svg>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.24em] text-slate-500">
                      Download on
                    </span>
                    <span className="block text-base font-semibold leading-tight">
                      iOS
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <img
            className="w-full mt-8 md:mt-0 object-fill md:w-2/3 md:-ml-4 lg:-ml-4 xl:ml-0 rounded-3xl shadow-gray-900 shadow-2xl"
            src={process.env.PUBLIC_URL + "/assets/images/hero.jpg"}
            alt="sample page"
            role="img"
          />
        </div>
      </div>
      {iosComingSoonOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ios-coming-soon-title"
          onClick={() => setIosComingSoonOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-white">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M16.6 12.4c0-2.1 1.7-3.1 1.8-3.2-1-1.5-2.5-1.7-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.6-.8-2.7-.8-1.4 0-2.7.8-3.5 2.1-1.5 2.7-.4 6.6 1.1 8.8.7 1.1 1.6 2.3 2.7 2.2 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1.1 2.7-2.2.8-1.2 1.1-2.3 1.1-2.4-.1 0-2.7-1-2.7-3.6Z" />
                <path d="M14.6 6.1c.6-.8 1.1-1.8.9-2.8-.9 0-2 .6-2.6 1.3-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.6-1.2Z" />
              </svg>
            </div>
            <h2 id="ios-coming-soon-title" className="text-2xl font-bold text-slate-950">
              iOS app coming soon
            </h2>
            <p className="mt-3 text-slate-600">
              CaloriesPal for iPhone is on the way. The Android app is available now on Google Play.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href={googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleGooglePlayClick}
                className="inline-flex flex-1 items-center justify-center rounded-2xl bg-lime-500 px-5 py-3 font-semibold text-slate-950 transition hover:bg-lime-400"
              >
                Open Google Play
              </a>
              <button
                type="button"
                onClick={() => setIosComingSoonOpen(false)}
                className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white py-12 px-4 xl:px-0">
        <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-2 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-lime-500 mb-2">
              Open Food Library
            </p>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Browse every ingredient without searching.
            </h2>
            <p className="text-gray-600 mb-4">
              Prefer to explore? Our public food page lists every ingredient, nutrition fact, and
              macro profile in a single interface, ready for both humans and search engines. No
              login or query required—just tap the Foods tab and start scrolling.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Alphabetized ingredient index with direct detail pages</li>
              <li>• Nutrition flags highlight calories, protein, fiber, and sodium instantly</li>
              <li>• Quick export lets you download the full nutrient table for offline analysis</li>
            </ul>
          </div>
          <div className="bg-slate-900 text-white rounded-3xl p-6 shadow-2xl">
            <h3 className="text-2xl font-semibold mb-3">Start with our foods directory</h3>
            <p className="text-sm text-slate-300 mb-5">
              Jump straight into every ingredient we publish. Browse inspiration, compare nutrition
              stats, and open any food’s detail page with one tap.
            </p>
            <a
              href="/foods"
              className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-lime-400 to-emerald-400 text-slate-900 font-semibold px-6 py-3 shadow-lg hover:opacity-90 transition"
            >
              Visit the Foods directory
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .top-100 {
            animation: slideDown .5s ease-in-out;
        }

        @keyframes slideDown {
            0% {
                top: -50%;
            }

            100% {
                top: 0;
            }
        }

        .cta-heartbeat {
            animation: heartbeat 1.8s ease-in-out infinite;
            box-shadow: 0 0 0 0 rgba(132, 204, 22, 0.45);
        }

        .cta-heartbeat:hover {
            animation-play-state: paused;
        }

        @keyframes heartbeat {
            0% {
                transform: scale(1);
                box-shadow: 0 0 0 0 rgba(132, 204, 22, 0.3);
            }
            30% {
                transform: scale(1.06);
                box-shadow: 0 0 0 8px rgba(132, 204, 22, 0);
            }
            45% {
                transform: scale(0.98);
            }
            60% {
                transform: scale(1.04);
                box-shadow: 0 0 0 4px rgba(132, 204, 22, 0.15);
            }
            100% {
                transform: scale(1);
                box-shadow: 0 0 0 0 rgba(132, 204, 22, 0);
            }
        }

        * {
            outline: none !important;
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            -webkit-tap-highlight-color: transparent;
        } `}</style>
      {/* ##### numbers */}
      <div>
        <div className="pb-20">
          <div className="mx-auto bg-lime-500 h-96">
            <div className="mx-auto container w-full flex flex-col justify-center items-center">
              <div className="flex justify-center items-center flex-col">
                <div className="mt-20">
                  <h2 className="lg:text-6xl md:text-5xl text-4xl font-black leading-10 text-white">
                    By The Numbers
                  </h2>
                </div>
                <div className="mt-6 mx-2 md:mx-0 text-center">
                  <p className="lg:text-lg md:text-base leading-6 text-sm  text-white">
                    Nutrition Made Simple
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mx-auto container md:-mt-28 -mt-20 flex justify-center items-center">
            <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-2 gap-x-2 gap-y-2 lg:gap-x-6 md:gap-x-6 md:gap-y-6 md:gap-y-6">
              <div className="flex justify-center flex-col items-center w-36 h-36 md:w-44 md:h-48 lg:w-56 lg:h-56 bg-white shadow rounded-2xl shadow-xl shadow-lime-400">
                <h2 className="lg:text-5xl md:text-4xl text-2xl font-extrabold leading-10 text-center text-gray-800">
                  40+
                </h2>
                <p className="mt-4 text-sm md:text-base lg:text-lg leading-none text-center text-gray-600">
                  Happy Clients
                </p>
              </div>
              <div className="flex justify-center flex-col items-center w-36 h-36 md:w-44 md:h-48 lg:w-56 lg:h-56 bg-white shadow rounded-2xl shadow-xl shadow-lime-400">
                <h2 className="lg:text-5xl md:text-4xl text-2xl font-extrabold leading-10 text-center text-gray-800">
                  500K+
                </h2>
                <p className="mt-4 text-sm md:text-base lg:text-lg leading-none text-center text-gray-600">
                  Ingredients
                </p>
              </div>
              <div className="flex justify-center flex-col items-center w-36 h-36 md:w-44 md:h-48 lg:w-56 lg:h-56 bg-white shadow rounded-2xl shadow-xl shadow-lime-400">
                <h2 className="lg:text-5xl md:text-4xl text-2xl font-extrabold leading-10 text-center text-gray-800">
                  10K+
                </h2>
                <p className="mt-4 text-sm md:text-base lg:text-lg leading-none text-center text-gray-600">
                  API Requests/hour
                </p>
              </div>
              <div className="flex justify-center flex-col items-center w-36 h-36 md:w-44 md:h-48 lg:w-56 lg:h-56 bg-white shadow rounded-2xl shadow-xl shadow-lime-400">
                <h2 className="lg:text-5xl md:text-4xl text-2xl font-extrabold leading-10 text-center text-gray-800">
                  100+
                </h2>
                <p className="mt-4 text-sm md:text-base lg:text-lg leading-none text-center text-gray-600">
                  Recipes
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ####services  */}
      <div className="overflow-y-hidden" id="services" ref={servicesBlock}>
        <div className="pb-16" style={{ fontFamily: '"Lato", sans-serif' }}>
          {/* Code block starts */}
          <dh-component>
            <section className="mx-auto container bg-white pt-16">
              <div className="px-4 lg:px-0">
                <div
                  role="contentinfo"
                  className="flex items-center flex-col px-4"
                >
                  <p
                    tabIndex={0}
                    className="focus:outline-none uppercase text-sm text-center text-gray-500 leading-none"
                  >
                    Services
                  </p>
                  <h1
                    tabIndex={0}
                    className="focus:outline-none text-4xl lg:text-4xl pt-4 font-extrabold text-center leading-tight text-gray-800 lg:w-7/12 md:w-9/12 xl:w-5/12"
                  >
                    Transforming Your Business With Our Expert Services
                  </h1>
                </div>
              </div>
            </section>
            <section className="bg-white py-12 mt-12">
              <div
                tabIndex={0}
                aria-label="group of cards"
                className="focus:outline-none px-4 lg:px-0"
              >
                <div className="mx-auto container flex flex-wrap px-2 lg:px-24">
                  <div
                    tabIndex={0}
                    aria-label="card 1"
                    className="focus:outline-none flex sm:w-full md:w-1/2 lg:border-r md:border-r sm:border-r-0 border-lime-300 pb-10 lg:pt-10"
                  >
                    <div className=" flex flex-shrink-0 mr-5">
                      <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-lime-50 text-lime-600 shadow-inner shadow-lime-200/60">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8"
                        >
                          <rect
                            x="3"
                            y="5"
                            width="18"
                            height="14"
                            rx="2"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M3 10H21"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8 15H10.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="md:w-9/12 lg:w-9/12">
                      <h2
                        tabIndex={0}
                        className="focus:outline-none text-lg font-semibold leading-5 text-black"
                      >
                        Your Subscription
                      </h2>
                      <p
                        tabIndex={0}
                        className="focus:outline-none text-base text-black leading-normal xl:w-10/12 pt-2"
                      >
                        <ul className="list-outside list-disc ml-6 text-black font-medium">
                          <li>Unlimited Recipe Creation</li>
                          <li>Recipe Costing Tool</li>
                          <li>Nutrition Facts Labels</li>
                          <li>Arabic & English Labels</li>
                          <li>USDA Approved Data </li>
                          <li>Support 24/7</li>
                        </ul>
                      </p>
                    </div>
                  </div>
                  <div
                    tabIndex={0}
                    aria-label="card 2"
                    className="focus:outline-none flex sm:w-full md:w-1/2 lg:pb-10 lg:pt-10"
                  >
                    <div className=" flex flex-shrink-0 sm:ml-0 md:ml-10 lg:ml-10 mr-5">
                      <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 shadow-inner shadow-sky-200/60">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8"
                        >
                          <path
                            d="M12 3L13.89 8.26L19.5 8.27L15 11.74L16.9 17L12 13.77L7.1 17L9 11.74L4.5 8.27L10.11 8.26L12 3Z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="md:w-9/12 lg:w-9/12 ">
                      <h2
                        tabIndex={0}
                        className="focus:outline-none text-lg font-semibold leading-5 text-black"
                      >
                        Added Value Services
                      </h2>
                      <p
                        tabIndex={0}
                        className="focus:outline-none text-base text-black leading-normal xl:w-10/12 pt-2"
                      >
                        <ul className="list-outside list-disc ml-6 text-black font-medium">
                          <li>Menu Consultation</li>
                          <li>Operation Audit</li>
                          <li>Up to 15 Recipes Free Data Entry</li>
                          <li>Culinary & Nutrition Training for your team</li>
                        </ul>
                      </p>
                    </div>
                  </div>
                  <div
                    tabIndex={0}
                    aria-label="card 3"
                    className="focus:outline-none flex sm:w-full md:w-1/2 lg:border-t md:border-t sm:border-t-0 lg:border-r md:border-r sm:border-r-0 border-lime-300 pt-10 lg:pb-20"
                  >
                    <div className=" flex flex-shrink-0 mr-5">
                      <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 shadow-inner shadow-amber-200/60">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8"
                        >
                          <path
                            d="M4 7H20"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M6 11H18"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M9 15H15"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12 3V7"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12 15V21"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="md:w-9/12 lg:w-9/12 ">
                      <h2
                        tabIndex={0}
                        className="focus:outline-none text-lg font-semibold leading-5 text-black"
                      >
                        Additional Services
                      </h2>
                      <p
                        tabIndex={0}
                        className="focus:outline-none text-base text-black leading-normal xl:w-10/12 pt-2"
                      >
                        <ul className="list-outside list-disc ml-6 text-black font-medium">
                          <li> Menu Development</li>
                          <li>Menu Costing</li>
                          <li>Operation Training</li>
                          <li>Front of House Training</li>
                        </ul>
                      </p>
                    </div>
                  </div>
                  <div
                    tabIndex={0}
                    aria-label="card 4"
                    className="focus:outline-none flex sm:w-full md:w-1/2 lg:border-t md:border-t sm:border-t-0 border-lime-300 pt-10 lg:pb-20"
                  >
                    <div className=" flex flex-shrink-0 sm:ml-0 md:ml-10 lg:ml-10 mr-5">
                      <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner shadow-indigo-200/60">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8"
                        >
                          <path
                            d="M12 21C12 21 4 17 4 9.5C4 6.46243 6.23858 4 9 4C10.635 4 12 5 12 5C12 5 13.365 4 15 4C17.7614 4 20 6.46243 20 9.5C20 17 12 21 12 21Z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 11.5C13.1046 11.5 14 10.6046 14 9.5C14 8.39543 13.1046 7.5 12 7.5C10.8954 7.5 10 8.39543 10 9.5C10 10.6046 10.8954 11.5 12 11.5Z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="md:w-9/12 lg:w-9/12 ">
                      <h2
                        tabIndex={0}
                        className="focus:outline-none text-lg font-semibold leading-5 text-black"
                      >
                        Technical Services
                      </h2>
                      <p
                        tabIndex={0}
                        className="focus:outline-none text-base text-black leading-normal xl:w-10/12 pt-2"
                      >
                        <ul className="list-outside list-disc ml-6 text-black font-medium">
                          <li>
                            Free migration to make switching easy and
                            stress-free
                          </li>
                          <li>Daily Backups</li>
                          <li>API Subscription</li>
                          <li>Your data is 100% secure</li>
                        </ul>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </dh-component>
          {/* Code block ends */}
        </div>
      </div>
      {/* #### how it works */}
      <div classname="overflow-y-hidden bg-gray-100" ref={howitworksBlock}>
        <div className="mx-auto container f-f-p px-4 xl:px-0 ">
          <h1 className="focus:outline-none text-center text-3xl lg:text-4xl font-extrabold lg:leading-9 tracking-wider text-gray-900">
            How it works?
          </h1>
          <div className="md:mt-24 f-f-p">
            <div className="hidden md:flex justify-center w-full">
              <div className="flex flex-col items-center md:items-end md:pr-12 md:border-r border-lime-300 relative md:w-1/2">
                <div aria-label="sign up" role="img">
                  <img
                    className="focus:outline-none mt-10"
                    src={
                      process.env.PUBLIC_URL + "/assets/images/howsignup.svg"
                    }
                    alt="how it work"
                  />
                </div>
                <div aria-label={2} role="img">
                  <img
                    className="focus:outline-none mt-24"
                    src={process.env.PUBLIC_URL + "/assets/images/howto2.svg"}
                  />
                </div>
                <div className="flex mt-12 flex-col items-center lg:items-end md:w-8/12">
                  <h1 className="focus:outline-none text-xl font-bold leading-5">
                    Create a Recipe
                  </h1>
                  <h2 className="focus:outline-none text-gray-500 mt-3 pl-3 text-center md:text-right text-base leading-6 tracking-wide">
                    Create recipes and start add ingredients and access the
                    Nutrition facts labels and stastics.
                  </h2>
                </div>
                <div aria-label="transactions" role="img">
                  <img
                    className="focus:outline-none mt-24"
                    src={
                      process.env.PUBLIC_URL + "/assets/images/howtoprint.svg"
                    }
                  />
                </div>
              </div>
              <div className="flex flex-col items-center md:items-start md:pl-12 lg:border-gray-400 mt-20 md:mt-0 md:w-1/2">
                <div aria-label={1} role="img">
                  <img
                    src={process.env.PUBLIC_URL + "/assets/images/howto1.svg"}
                  />
                </div>
                <div className="flex mt-6 flex-col items-center md:items-start md:w-8/12">
                  <h1 className="focus:outline-none text-xl font-bold leading-5">
                    SignUp for an Account
                  </h1>
                  <h2 className="focus:outline-none text-gray-500 mt-3 text-base leading-6 tracking-wide">
                    Sign up for an account with us to access exclusive features
                    and benefits!
                  </h2>
                </div>
                <div aria-label="wallet" role="img">
                  <img
                    className="focus:outline-none mt-32"
                    src={
                      process.env.PUBLIC_URL +
                      "/assets/images/howtoaddrecipe.svg"
                    }
                  />
                </div>
                <div aria-label={3} role="img">
                  <img
                    className="focus:outline-none mt-20"
                    src={process.env.PUBLIC_URL + "/assets/images/howto3.svg"}
                  />
                </div>
                <div className="flex mt-6 flex-col items-center md:items-start md:w-8/12">
                  <h1 className="focus:outline-none text-xl font-bold leading-5">
                    Print Your Labels
                  </h1>
                  <h2 className="focus:outline-none text-gray-500 mt-3 text-base leading-6 tracking-wide">
                    Start Printing right away. No more complicated process for
                    anything else.
                  </h2>
                </div>
              </div>
            </div>
            <div className="md:hidden flex flex-col items-center w-full">
              <img
                className="focus:outline-none my-10"
                src={process.env.PUBLIC_URL + "/assets/images/howsignup.svg"}
                alt="how it work"
              />
              <img src={process.env.PUBLIC_URL + "/assets/images/howto1.svg"} />
              <div className="mt-10">
                <h1 className="text-xl text-center tracking-wide leading-5 font-bold">
                  SignUp for an Account
                </h1>
                <h2 className="text-gray-500 mt-3 text-center text-base leading-6 tracking-wide">
                  Sign up for an account with us to access exclusive features
                  and benefits!
                </h2>
              </div>
              <img
                className="focus:outline-none my-10"
                src={
                  process.env.PUBLIC_URL + "/assets/images/howtoaddrecipe.svg"
                }
              />
              <img src={process.env.PUBLIC_URL + "/assets/images/howto2.svg"} />
              <div className="mt-10">
                <h1 className="text-xl tracking-wide text-center leading-5 font-bold">
                  Create a Recipe
                </h1>
                <h2 className="text-gray-500 mt-3 pl-3 text-center text-base leading-6 tracking-wide">
                  Create a recipe by and start add ingredients and access the
                  nutrition facts labels and stastics.
                </h2>
              </div>
              <img
                className="focus:outline-none my-10"
                src={process.env.PUBLIC_URL + "/assets/images/howtoprint.svg"}
              />
              <img
                className
                src={process.env.PUBLIC_URL + "/assets/images/howto3.svg"}
              />
              <div className="flex mt-10 flex-col items-center md:items-start md:w-8/12">
                <h1 className="text-xl text-center tracking-wide leading-5 font-bold">
                  Print Your Labels
                </h1>
                <h2 className="text-gray-500 mt-3 text-center text-base leading-6 tracking-wide">
                  Start Printing right away. No more complicated process for
                  anything else.
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ### contact form */}
      <form onSubmit={handleSubmit}>
        <div
          className="w-full flex items-center justify-center mt-24  "
          ref={contactusBlock}
        >
          <div className=" bg-white shadow rounded py-12 lg:px-28 px-8 shadow-inner shadow-lime-300 drop-shadow-xl">
            <p className="md:text-3xl text-xl font-bold leading-7 text-center text-gray-700">
              Let’s chat and get a quote!
            </p>
            <div className="md:flex items-center mt-12">
              <div className="md:w-72 flex flex-col">
                <label className="text-base font-semibold leading-none text-gray-800">
                  Name
                </label>
                <input
                  tabIndex={0}
                  arial-label="Please input name"
                  type="name"
                  id="name"
                  className="text-base leading-none text-gray-900 p-3 focus:oultine-none focus:border-lime-300 mt-4 bg-gray-100 border rounded border-gray-200 placeholder-gray-100"
                  placeholder="Please input  name"
                />
              </div>
              <div className="md:w-72 flex flex-col md:ml-6 md:mt-0 mt-4">
                <label className="text-base font-semibold leading-none text-gray-800">
                  Email Address
                </label>
                <input
                  tabIndex={0}
                  arial-label="Please input email address"
                  type="email"
                  id="email"
                  className="text-base leading-none text-gray-900 p-3 focus:oultine-none focus:border-lime-300 mt-4 bg-gray-100 border rounded border-gray-200 placeholder-gray-100"
                  placeholder="Please input email address"
                />
              </div>
            </div>
            <div className="md:flex items-center mt-8">
              <div className="md:w-72 flex flex-col">
                <label className="text-base font-semibold leading-none text-gray-800">
                  Business name
                </label>
                <input
                  tabIndex={0}
                  role="input"
                  arial-label="Please input company name"
                  type="name"
                  id="company"
                  className="text-base leading-none text-gray-900 p-3 focus:oultine-none focus:border-lime-300 mt-4 bg-gray-100 border rounded border-gray-200 placeholder-gray-100 "
                  placeholder="Please input company name"
                />
              </div>
              <div className="md:w-72 flex flex-col md:ml-6 md:mt-0 mt-4">
                <label className="text-base font-semibold leading-none text-gray-800">
                  Country
                </label>
                <input
                  tabIndex={0}
                  arial-label="Please input country name"
                  type="name"
                  id="country"
                  className="text-base leading-none text-gray-900 p-3 focus:oultine-none focus:border-lime-300 mt-4 bg-gray-100 border rounded border-gray-200 placeholder-gray-100"
                  placeholder="Please input country name"
                />
              </div>
            </div>
            <div>
              <div className="w-full flex flex-col mt-8">
                <label className="text-base font-semibold leading-none text-gray-800">
                  Message
                </label>
                <textarea
                  tabIndex={0}
                  aria-label="leave a message"
                  role="textbox"
                  id="message"
                  type="name"
                  className="h-36 text-base leading-none text-gray-900 p-3 focus:oultine-none focus:border-lime-300 mt-4 bg-gray-100 border rounded border-gray-200 placeholder-gray-100 resize-none"
                  defaultValue={""}
                />
              </div>
            </div>
            <p className="text-xs leading-3 text-gray-600 mt-4">
              By clicking submit you agree to our terms of service, privacy
              policy and how we use data as stated
            </p>
            <div className="flex items-center justify-center w-full">
              <button
                type="submit"
                className="mt-9 text-base font-semibold leading-none text-white py-4 px-10 bg-lime-500 rounded hover:bg-lime-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700 focus:outline-none"
              >
                {status}
              </button>
            </div>
          </div>
        </div>
      </form>
      {/* ####footer */}

      <div className>
        <div className="relative flex justify-start md:justify-center md:items-end ">
          <img
            className="absolute object-cover  top-10 h-full w-full xl:mt-10 z-0"
            src={process.env.PUBLIC_URL + "/assets/images/foote_background.png"}
            alt="background"
          />
          <div className="flex pt-36 md:pt-32 lg:pt-40 xl:pt-96   px-4 md:px-6  xl:px-20 flex-col justify-start items-start md:justify-center md:items-center relative z-10">
            <div className="flex  flex-col items-start justify-start xl:justify-center xl:space-x-8 xl:flex-row">
              <div className="flex justify-start items-center space-x-4">
                <div className="cursor-pointer  bg-neutral-200 rounded-3xl">
                  <img
                    src={
                      process.env.PUBLIC_URL + "/assets/images/Calories (1).png"
                    }
                    alt="logo"
                  />
                </div>
                <p className="w-60 text-xl xl:text-2xl font-semibold leading-normal text-white"></p>
              </div>
              <div className="mt-12 xl:mt-0 grid grid-cols-1 sm:grid-cols-3 gap-y-12 sm:gap-y-0 w-full md:w-auto sm:gap-x-20 md:gap-x-28 xl:gap-8">
                <div className="sm:w-40 md:w-auto xl:w-72 flex justify-start items-start flex-col space-y-6">
                  <h2 className="text-base xl:text-xl font-bold xl:font-semibold leading-4 xl:leading-5 text-white">
                    Information
                  </h2>
                  <div>
                    <ul className=" text-white">
                      <li className="flex space-x-2 pb-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          className="w-6 h-6 "
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z"
                          />
                        </svg>
                        <p>contact@caloriespal.com</p>
                      </li>
                      <li className="flex space-x-2 pb-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          className="w-6 h-6"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                          />
                        </svg>

                        <p>Dubai, UAE.</p>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="sm:w-40 md:w-auto xl:w-72 flex justify-start items-start flex-col space-y-6">
                  <h2 className="text-base xl:text-xl font-bold xl:font-semibold leading-4 xl:leading-5 text-white">
                    Getting Started
                  </h2>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        behavior: "smooth",
                        top: contactusBlock.current.offsetTop,
                      });
                    }}
                    className="text-left text-base hover:text-gray-400 leading-none text-gray-100"
                  >
                    Signup!
                  </button>
                  <button
                    onClick={handleSignin}
                    className="text-left text-base hover:text-gray-400 leading-none text-gray-100"
                  >
                    Signin
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        behavior: "smooth",
                        top: servicesBlock.current.offsetTop,
                      });
                    }}
                    className="text-left text-base hover:text-gray-400 leading-none text-gray-100"
                  >
                    Services
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({
                        behavior: "smooth",
                        top: howitworksBlock.current.offsetTop,
                      });
                    }}
                    className="text-left text-base hover:text-gray-400 leading-none text-gray-100"
                  >
                    How it Works
                  </button>
                  <button
                    onClick={() => navigate("/privacy-policy")}
                    className="text-left text-base hover:text-gray-400 leading-none text-gray-100"
                  >
                    Privacy Policy
                  </button>
                  <button
                    onClick={() => navigate("/delete-account-request")}
                    className="text-left text-base hover:text-gray-400 leading-none text-gray-100"
                  >
                    Delete Account Request
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-12 flex  xl:justify-between xl:flex-row flex-col-reverse items-center xl:items-start w-full ">
              <p className="mt-10 md:mt-12 xl:mt-0 text-sm leading-none text-white">
                2023 Calories Pal Company. All Rights Reserved
              </p>
              <div className="mt-10 md:mt-12 xl:mt-0 md:flex-row flex-col flex md:justify-center w-full md:w-auto justify-start items-start space-y-4 md:space-y-0 md:items-center md:space-x-4 xl:space-x-6"></div>
              <div className="flex  justify-start md:justify-end items-start  w-full md:w-auto md:items-center space-x-6 ">
                <button className="text-white hover:text-gray-200 w-6">
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M23.25 5.13282C22.406 5.49955 21.513 5.74116 20.5992 5.85001C21.5595 5.28769 22.2817 4.39434 22.6303 3.33751C21.7224 3.86841 20.7307 4.24092 19.6978 4.43907C19.2629 3.98322 18.7397 3.62059 18.1603 3.3732C17.5808 3.12581 16.9571 2.99884 16.327 3.00001C13.7761 3.00001 11.7117 5.03438 11.7117 7.5422C11.7099 7.89102 11.7499 8.23881 11.8308 8.57813C10.0016 8.49238 8.2104 8.02575 6.57187 7.2081C4.93333 6.39044 3.48351 5.23977 2.31516 3.8297C1.90527 4.52069 1.6885 5.30909 1.6875 6.11251C1.6875 7.68751 2.50922 9.0797 3.75 9.89532C3.01487 9.87787 2.29481 9.68331 1.65094 9.32813V9.38438C1.65094 11.5875 3.24469 13.4203 5.35406 13.8375C4.9574 13.9433 4.54864 13.9968 4.13812 13.9969C3.84683 13.9974 3.5562 13.9691 3.27047 13.9125C3.85687 15.7172 5.56359 17.0297 7.58531 17.0672C5.94252 18.3333 3.9256 19.0175 1.85156 19.0125C1.48341 19.012 1.11561 18.99 0.75 18.9469C2.85993 20.2942 5.31255 21.0068 7.81594 21C16.3172 21 20.9616 14.0766 20.9616 8.07188C20.9616 7.87501 20.9564 7.67813 20.947 7.48595C21.8485 6.84472 22.6283 6.04787 23.25 5.13282V5.13282Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button className="text-white hover:text-gray-200 w-6">
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M22.5 12.0645C22.5 6.26602 17.7984 1.56445 12 1.56445C6.20156 1.56445 1.5 6.26602 1.5 12.0645C1.5 17.3051 5.33906 21.649 10.3594 22.4374V15.1005H7.69266V12.0645H10.3594V9.75117C10.3594 7.12008 11.9273 5.66555 14.3255 5.66555C15.4744 5.66555 16.6763 5.87086 16.6763 5.87086V8.45508H15.3516C14.048 8.45508 13.6402 9.26414 13.6402 10.0957V12.0645H16.552L16.087 15.1005H13.6406V22.4384C18.6609 21.6504 22.5 17.3065 22.5 12.0645Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button className="text-white hover:text-gray-200 w-6">
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 1.5C6.20297 1.5 1.5 6.20297 1.5 12C1.5 17.797 6.20297 22.5 12 22.5C17.797 22.5 22.5 17.8022 22.5 12C22.5 6.19781 17.797 1.5 12 1.5ZM18.6666 6.33984C19.8815 7.76805 20.6046 9.54925 20.7291 11.4202C18.8367 11.3217 16.5727 11.3217 14.7572 11.4914C14.5275 10.9116 14.2922 10.343 14.0353 9.79594C16.1288 8.88844 17.7422 7.69594 18.6666 6.33984ZM12 3.24984C14.0395 3.24634 16.0156 3.95866 17.5837 5.26266C16.6322 6.46547 15.1392 7.51266 13.2797 8.30297C12.2625 6.42188 11.092 4.80328 9.84375 3.52875C10.5482 3.3469 11.2725 3.25322 12 3.24984V3.24984ZM8.04047 4.20703C9.30375 5.46469 10.4906 7.06641 11.5298 8.94141C9.55547 9.54469 7.29141 9.89062 4.875 9.89062C4.41562 9.89062 3.9675 9.87422 3.52453 9.84656C3.83266 8.63935 4.3949 7.5118 5.17367 6.53927C5.95244 5.56673 6.92981 4.77161 8.04047 4.20703V4.20703ZM3.26625 11.5842C3.76406 11.6063 4.26703 11.617 4.77562 11.6119C7.53187 11.5791 10.0969 11.1469 12.3009 10.44C12.5088 10.8722 12.7056 11.3152 12.8916 11.7689C12.6252 11.8272 12.3637 11.9056 12.1092 12.0033C9.09609 13.2689 6.72281 15.3084 5.4375 17.775C4.02813 16.1814 3.25007 14.1274 3.24984 12C3.24984 11.8594 3.25547 11.7211 3.26625 11.5842ZM12 20.7502C10.056 20.7525 8.16724 20.1036 6.63516 18.907C7.8 16.5994 9.87797 14.6883 12.4978 13.5037C12.7439 13.3889 13.072 13.2961 13.4494 13.2141C13.7619 14.0778 14.0408 14.9747 14.2861 15.9047C14.6525 17.3083 14.9223 18.7354 15.0938 20.1759C14.1061 20.5543 13.0576 20.7489 12 20.7502V20.7502ZM16.7306 19.3556C16.5595 17.9001 16.2871 16.4584 15.9155 15.0408C15.728 14.3297 15.5166 13.6406 15.2869 12.968C17.0039 12.8273 19.0603 12.8475 20.6953 12.968C20.5512 14.2606 20.1206 15.5047 19.4347 16.6097C18.7488 17.7148 17.825 18.6529 16.7306 19.3556V19.3556Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button className="text-white hover:text-gray-200 w-6">
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 1.5C6.20156 1.5 1.5 6.32344 1.5 12.2672C1.5 17.025 4.50937 21.0562 8.68125 22.4812C8.73977 22.494 8.79949 22.5002 8.85938 22.5C9.24844 22.5 9.39844 22.2141 9.39844 21.9656C9.39844 21.7078 9.38906 21.0328 9.38437 20.1328C9.03705 20.2142 8.68173 20.2567 8.325 20.2594C6.30469 20.2594 5.84531 18.6891 5.84531 18.6891C5.36719 17.4469 4.67813 17.1141 4.67813 17.1141C3.76406 16.4719 4.67344 16.4531 4.74375 16.4531H4.74844C5.80313 16.5469 6.35625 17.5687 6.35625 17.5687C6.88125 18.4875 7.58437 18.7453 8.2125 18.7453C8.62783 18.737 9.03673 18.6412 9.4125 18.4641C9.50625 17.7703 9.77812 17.2969 10.0781 17.025C7.74844 16.7531 5.29688 15.8297 5.29688 11.7047C5.29688 10.5281 5.70469 9.56719 6.375 8.81719C6.26719 8.54531 5.90625 7.44844 6.47812 5.96719C6.55483 5.94883 6.63368 5.94095 6.7125 5.94375C7.09219 5.94375 7.95 6.08906 9.36563 7.07344C11.0857 6.59218 12.9049 6.59218 14.625 7.07344C16.0406 6.08906 16.8984 5.94375 17.2781 5.94375C17.357 5.94095 17.4358 5.94883 17.5125 5.96719C18.0844 7.44844 17.7234 8.54531 17.6156 8.81719C18.2859 9.57187 18.6937 10.5328 18.6937 11.7047C18.6937 15.8391 16.2375 16.7484 13.8984 17.0156C14.2734 17.3484 14.6109 18.0047 14.6109 19.0078C14.6109 20.4469 14.5969 21.6094 14.5969 21.9609C14.5969 22.2141 14.7422 22.5 15.1312 22.5C15.1942 22.5003 15.2571 22.494 15.3187 22.4812C19.4953 21.0562 22.5 17.0203 22.5 12.2672C22.5 6.32344 17.7984 1.5 12 1.5Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
